import React, { useState } from "react";
import { Typography } from "@/components/ui/typography";
import SearchDialogBox from "@/components/common/SearchDialogBox";
import crossIcon from "@/assets/svg/crossIcon.svg";
import crossDarkIcon from "@/assets/svg/crossDarkIcon.svg";
import CardItemComponent from "@/components/common/CardItemComponent";
import { CARD_REGISTRY } from "@/constants/cardListDescription";
import { CardSkeleton } from "@/components/ui/skeleton";
import NoRecordsDrawerLight from "@/assets/svg/NoRecordsDrawerLight.svg";
import NoRecordsDrawerDark from "@/assets/svg/NoRecordsDrawerDark.svg";

export interface CardItem {
  id: string;
  title: string;
  subtitle: string;
  icon: { light: string; dark: string };
  isAdded: boolean;
}

interface DrawerProps {
  onClose: () => void;
  title?: string;
  cards?: CardItem[];
  onAdd?: (cardId: string) => void;
  onRemove?: (cardId: string) => void;
  addedCardIds?: string[];
  apiCards?: any[] | null;
  loading?: boolean;
}

const Drawer: React.FC<DrawerProps> = ({
  onClose,
  title = "Add Cards",
  cards: propCards,
  onAdd,
  onRemove,
  addedCardIds = [],
  apiCards,
  loading = false,
}) => {
  const [searchValue, setSearchValue] = useState("");

  const cards: CardItem[] = React.useMemo(() => {
    if (propCards) return propCards;
    if (!apiCards) return [];

    return apiCards
      .filter((apiCard: any) => apiCard?.layoutPosition !== "fixed")
      .map((apiCard: any) => {
        const id = apiCard?.type || "";
        const registryEntry = CARD_REGISTRY[id];

        return {
          id,
          title: apiCard?.displayTitle || "-",
          subtitle: apiCard?.desc || "-",
          icon: registryEntry?.icon || { light: "", dark: "" },
          isAdded: false, // Will be updated in filteredCards
        };
      });
  }, [apiCards, propCards]);

  const filteredCards = cards
    .map(card => ({ ...card, isAdded: addedCardIds.includes(card.id) }))
    .filter(
      (card) =>
        card.title.toLowerCase().startsWith(searchValue.toLowerCase())
    )
    .sort((a, b) => a.title.localeCompare(b.title));

  return (
    <>
      {/* Drawer panel */}
      <div className="flex flex-col h-full bg-white dark:bg-blue-900 border-l border-grey-200 dark:border-grey-700 min-w-[500px]">
        {/* Header */}
        <div className='flex items-center justify-between px-4 py-4'>
          <Typography
            variant='semibold-16'
            className='text-grey-800 dark:text-white'>
            {title}
          </Typography>
          <div
            role='button'
            aria-label='Close'
            onClick={onClose}
            className='
              h-[30px] w-[30px]
              bg-grey-200
              hover:bg-grey-300
              dark:bg-[#32414A]
              dark:hover:bg-gray-600
              flex items-center justify-center
              rounded-md
              cursor-pointer
              transition-all
              shrink-0
            '>
            <img src={crossIcon} alt='close' className='block dark:hidden' />
            <img src={crossDarkIcon} alt='close' className='hidden dark:block' />
          </div>
        </div>

        {/* Search */}
        <div className='px-4 py-3'>
          <SearchDialogBox
            value={searchValue}
            onChange={setSearchValue}
            placeholder='Search for a Card'
            width='100%'
          />
        </div>

        {/* Card list */}
        <div className='flex-1 overflow-y-auto overflow-x-hidden px-4'>
          {loading ? (
            <div className="py-4">
              <CardSkeleton rows={20} showProfile={false} />
            </div>
          ) : filteredCards.length > 0 ? (
            filteredCards.map((card) => (
              <CardItemComponent
                key={card.id}
                {...card}
                onAdd={onAdd}
                onRemove={onRemove}
              />
            ))
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center gap-4">
              <img src={NoRecordsDrawerLight} alt="No records" className="w-16 h-16 block dark:hidden" />
              <img src={NoRecordsDrawerDark} alt="No records" className="w-16 h-16 hidden dark:block" />
              <div className="flex flex-col gap-1">
                <Typography variant="semibold-16" className="text-grey-800 dark:text-grey-100">
                  No matching cards found
                </Typography>
                <Typography variant="regular-14" className="text-grey-600 dark:text-grey-500">
                  Try a different keyword or clear search to view all cards.
                </Typography>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Drawer;
